import React, { ComponentProps, FC, useContext } from "react";
import { type ViewProps } from "react-native";
import type { LucideIcon } from "lucide-react-native";

import { TextClassContext } from "@/context/TextClassContext";
import { cn } from "@/utils/tailwind";

import { VnBox } from "./VnBox";
import { VnIcon } from "./VnIcon";
import { VnText } from "./VnText/VnText";

type VnAlertPropsType = ViewProps & {
  icon: LucideIcon;
  variant?: "default" | "destructive";
  iconClassName?: string;
};

export const VnAlert: FC<VnAlertPropsType> = ({
  className,
  variant,
  children,
  icon,
  iconClassName,
  ...props
}) => {
  return (
    <TextClassContext.Provider
      value={cn(
        "text-foreground text-sm",
        variant === "destructive" && "text-destructive",
        className,
      )}>
      <VnBox
        role="alert"
        className={cn(
          "border-border bg-card relative w-full rounded-lg border px-4 pb-2 pt-3.5",
          className,
        )}
        {...props}>
        <VnBox className="absolute left-3.5 top-[15px] z-10">
          <VnIcon
            as={icon}
            className={cn(
              "size-4",
              variant === "destructive" && "text-destructive",
              iconClassName,
            )}
          />
        </VnBox>
        {children}
      </VnBox>
    </TextClassContext.Provider>
  );
};

type VnAlertTitlePropsType = ComponentProps<typeof VnText>;

export const VnAlertTitle: FC<VnAlertTitlePropsType> = ({
  className,
  ...props
}) => {
  return (
    <VnText
      className={cn(
        "font-primary-medium mb-1 ml-0.5 min-h-4 pl-6 leading-tight tracking-tight",
        className,
      )}
      {...props}
    />
  );
};

type VnAlertDescriptionPropsType = ComponentProps<typeof VnText>;

export const VnAlertDescription: FC<VnAlertDescriptionPropsType> = ({
  className,
  ...props
}) => {
  const textClass = useContext(TextClassContext);
  return (
    <VnText
      className={cn(
        "text-muted-foreground ml-0.5 pb-1.5 pl-6 text-sm leading-relaxed",
        textClass?.includes("text-destructive") && "text-destructive/90",
        className,
      )}
      {...props}
    />
  );
};
